import { Badge, Flex, Heading, Text } from '@radix-ui/themes';
import React, { useEffect, useState, useCallback } from 'react';
import CreateCaregiver from '../components/ui-components/CreateCaregiver';
import ViewUser from '../components/ui-components/ViewUser';
import TableComponent from '../components/ui-components/TableComponent';
import SearchFilter from '../components/ui-components/SearchFilter';
import PaginationComponent from '../components/ui-components/PaginationComponent';
import { MdOutlineVerifiedUser } from 'react-icons/md';
import axiosInstance from '../config/axiosConfig';
import { toast } from 'react-toastify';
import debounce from 'lodash/debounce';
import { highlightSearchTerm } from '../utils/helpers';

const CaregiverPage = () => {
  const roleId = localStorage.getItem('roleId');
  const [caregivers, setCaregivers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  const pageSize = 8;

  const columns = [
    {
      title: 'Sl No',
      key: 'serialNumber',
      render: (value) => <Text size="2">{value}</Text>,
    },
    {
      title: 'User Name',
      key: 'userName',
      render: (value, row) => (
        <Flex gap="2" align="center">
          <Flex direction="column">
            <Heading size="1">{highlightSearchTerm(value, searchQuery)}</Heading>
          </Flex>
        </Flex>
      ),
    },
    {
      title: 'Name',
      key: 'fullName',
      render: (value, row) => (
        <Text size="2">
          {highlightSearchTerm(`${row.firstName} ${row.lastName}`, searchQuery)}
        </Text>
      ),
    },
    {
      title: 'Employee ID',
      key: 'employeeId',
      render: (value) => <Text size="2">{highlightSearchTerm(value || 'N/A', searchQuery)}</Text>,
    },
    {
      title: 'Status',
      key: 'isActive',
      render: (value) => (
        <Badge color={value ? 'green' : 'tomato'}>
          <MdOutlineVerifiedUser color={value ? 'green' : 'red'} />
          {value ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (value, row) => (
        <Flex gap="2" align="center">
          <Flex direction="row" gap="1">
            <CreateCaregiver
              initialValues={row}
              roleId={roleId}
              onCaregiverCreated={() => fetchCaregivers(1)}
              showToast={showToast}
            />
            <ViewUser
              initialValues={row}
              onActionSuccess={() => fetchCaregivers(currentPage)}
            />
          </Flex>
        </Flex>
      ),
    },
  ];

  const fetchCaregivers = useCallback(
    async (page = 1, query = searchQuery, activeOnly = showActiveOnly) => {
      try {
        const payload = {
          searchTerm: query.length >= 3 ? query : null,
          isActive: activeOnly ? true : null,
          pageNumber: page,
          pageSize: pageSize,
          roleId: 4, // Caregivers
        };
        const response = await axiosInstance.post('/user/get-caregivers', payload);
        if (response.data && response.data.result && response.data.result.userGetList) {
          const caregiversData = response.data.result.userGetList.map((item, index) => ({
            ...item,
            serialNumber: (page - 1) * pageSize + index + 1,
            fullName: `${item.firstName} ${item.lastName}`,
          }));
          setCaregivers(caregiversData);
          setTotalCount(response.data.result.totalCount || caregiversData.length);
        } else {
          setCaregivers([]);
          setTotalCount(0);
          showToast('No caregivers found', 'error');
        }
      } catch (error) {
        console.error('Error fetching caregivers:', error);
        setCaregivers([]);
        setTotalCount(0);
        showToast('Failed to load caregivers', 'error');
      }
    },
    [searchQuery, showActiveOnly]
  );

  const debouncedFetch = useCallback(
    debounce((page, query, activeOnly) => {
      fetchCaregivers(page, query, activeOnly);
    }, 300),
    [fetchCaregivers]
  );

  useEffect(() => {
    debouncedFetch(currentPage, searchQuery, showActiveOnly);
    return () => debouncedFetch.cancel();
  }, [currentPage, searchQuery, showActiveOnly, debouncedFetch]);

  const handleSearchChange = (value) => {
    if (value.length === 0 || value.length >= 3) {
      setSearchQuery(value);
      setCurrentPage(1);
    }
  };

  const handleActiveToggle = () => {
    setShowActiveOnly(!showActiveOnly);
    setCurrentPage(1);
  };

  const showToast = (message, type = 'success') => {
    if (type === 'error') {
      toast.error(message);
      return;
    }
    toast.success(message);
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
      <Flex direction="column" gap="2">
        <Flex justify="between" align="center" mb="4" gap="3" wrap="wrap">
          <SearchFilter
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            showActiveOnly={showActiveOnly}
            setShowActiveOnly={setShowActiveOnly}
            placeholder="Search (min 3 chars)"
            onSearchChange={handleSearchChange}
            onActiveToggle={handleActiveToggle}
          />
          <Flex gap="3" align="center">
            <CreateCaregiver
              roleId={roleId}
              onCaregiverCreated={() => fetchCaregivers(1)}
              showToast={showToast}
            />
          </Flex>
        </Flex>
        <TableComponent dataStructure={caregivers} columns={columns} />
        {totalCount > 0 && (
          <PaginationComponent
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(newPage) => setCurrentPage(newPage)}
          />
        )}
        {totalCount > 0 && (
          <Text size="2" align="center" mt="2">
            Showing {(currentPage - 1) * pageSize + 1} -{' '}
            {Math.min(currentPage * pageSize, totalCount)} of {totalCount} caregivers
          </Text>
        )}
      </Flex>
  );
};

export default CaregiverPage;
